import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { API } from "@/config";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import AuthShell from "@/components/AuthShell";

const ERROR_MESSAGES = {
  google_denied: "Google sign-in was cancelled.",
  google_auth_failed: "Couldn't verify your Google account. Please try again.",
  email_not_verified: "Your Google account's email isn't verified. Please verify it with Google and try again.",
  microsoft_denied: "Microsoft sign-in was cancelled.",
  microsoft_auth_failed: "Couldn't verify your Microsoft account. Please try again.",
  microsoft_no_email: "Your Microsoft account doesn't have an email we can use. Please try a different sign-in method.",
  microsoft_not_configured: "Microsoft sign-in isn't available yet. Please use another sign-in method.",
  invalid_state: "Your sign-in session expired. Please try again.",
  missing_code: "Something went wrong. Please try again.",
  magic_link_expired: "That sign-in link is invalid or has expired. Please request a new one.",
};

const TITLES = {
  signin: { title: "Welcome back", subtitle: "Sign in to manage your club's passes." },
  signup: { title: "Create your account", subtitle: "Start free — no credit card required." },
  forgot: { title: "Reset your password", subtitle: "We'll email you a link to choose a new one." },
  magic: { title: "Sign in with a link", subtitle: "We'll email you a one-time sign-in link." },
};

const SUBMIT_LABELS = {
  signin: "Sign in",
  signup: "Create account",
  forgot: "Send reset link",
  magic: "Send sign-in link",
};

const oauthButtonClass =
  "w-full flex items-center justify-center gap-3 h-11 rounded-md border border-zinc-300 bg-white hover:bg-zinc-50 text-zinc-900 font-medium transition-colors";

const textLinkClass = "font-medium text-zinc-900 underline underline-offset-4 decoration-zinc-300 hover:decoration-zinc-900";

// Which of the four views is showing: sign in, create account, forgot
// password, or "email me a link" (magic link).
const LoginPage = () => {
  const [searchParams] = useSearchParams();
  const errorCode = searchParams.get("error");
  const urlError = errorCode ? (ERROR_MESSAGES[errorCode] || "Something went wrong. Please try again.") : "";

  const { login, signup, requestPasswordReset, requestMagicLink } = useAuth();

  const [mode, setMode] = useState("signin"); // signin | signup | forgot | magic
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const switchMode = (next) => {
    setMode(next);
    setError("");
    setNotice("");
  };

  const handleGoogleSignIn = () => {
    window.location.href = `${API}/auth/google/login`;
  };

  const handleMicrosoftSignIn = () => {
    window.location.href = `${API}/auth/microsoft/login`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setNotice("");
    setSubmitting(true);
    try {
      if (mode === "signin") {
        await login(email, password);
      } else if (mode === "signup") {
        await signup(email, password, name);
      } else if (mode === "forgot") {
        const data = await requestPasswordReset(email);
        setNotice(data.message);
      } else if (mode === "magic") {
        const data = await requestMagicLink(email);
        setNotice(data.message);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const showOAuth = mode === "signin" || mode === "signup";
  const inputClass = "mt-2 h-11 bg-white";

  return (
    <AuthShell title={TITLES[mode].title} subtitle={TITLES[mode].subtitle} testId="login-page">
      {(urlError || error) && (
        <div className="border-l-2 border-red-500 bg-red-50 text-red-700 text-sm px-4 py-3 mb-6" data-testid="login-error">
          {error || urlError}
        </div>
      )}
      {notice && (
        <div className="border-l-2 border-emerald-500 bg-emerald-50 text-emerald-800 text-sm px-4 py-3 mb-6" data-testid="login-notice">
          {notice}
        </div>
      )}

      {showOAuth && (
        <>
          <div className="space-y-3">
            <button type="button" onClick={handleGoogleSignIn} className={oauthButtonClass} data-testid="google-signin-btn">
              <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.9c1.7-1.57 2.68-3.87 2.68-6.62z" />
                <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.9-2.26c-.8.54-1.84.86-3.06.86-2.35 0-4.34-1.59-5.05-3.72H.96v2.33A9 9 0 0 0 9 18z" />
                <path fill="#FBBC05" d="M3.95 10.7A5.4 5.4 0 0 1 3.67 9c0-.59.1-1.17.28-1.7V4.97H.96A9 9 0 0 0 0 9c0 1.45.35 2.83.96 4.03l2.99-2.33z" />
                <path fill="#EA4335" d="M9 3.58c1.32 0 2.51.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.97l2.99 2.33C4.66 5.17 6.65 3.58 9 3.58z" />
              </svg>
              Continue with Google
            </button>
            <button type="button" onClick={handleMicrosoftSignIn} className={oauthButtonClass} data-testid="microsoft-signin-btn">
              <svg width="18" height="18" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
                <rect x="1" y="1" width="9" height="9" fill="#f25022" />
                <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
                <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
                <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
              </svg>
              Continue with Microsoft
            </button>
          </div>

          <div className="flex items-center gap-4 my-8">
            <div className="h-px flex-1 bg-zinc-200" />
            <span className="eyebrow">or with email</span>
            <div className="h-px flex-1 bg-zinc-200" />
          </div>
        </>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        {mode === "signup" && (
          <div>
            <Label className="text-sm text-zinc-700">Name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={inputClass}
              placeholder="Your name"
              data-testid="signup-name-input"
            />
          </div>
        )}

        <div>
          <Label className="text-sm text-zinc-700">Email</Label>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={inputClass}
            placeholder="you@example.com"
            required
            data-testid="login-email-input"
          />
        </div>

        {showOAuth && (
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-sm text-zinc-700">Password</Label>
              {mode === "signin" && (
                <button
                  type="button"
                  onClick={() => switchMode("forgot")}
                  className="text-sm text-zinc-500 hover:text-zinc-900"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </button>
              )}
            </div>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={inputClass}
              placeholder={mode === "signup" ? "At least 8 characters" : "••••••••"}
              required
              minLength={mode === "signup" ? 8 : undefined}
              data-testid="login-password-input"
            />
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="w-full h-11 rounded-md bg-[#303030] hover:bg-[#1A1A1A] text-white font-medium transition-colors disabled:opacity-60"
          data-testid="login-submit-btn"
        >
          {submitting ? "Please wait…" : SUBMIT_LABELS[mode]}
        </button>
      </form>

      <div className="mt-8 text-sm text-zinc-500 space-y-3">
        {mode === "signin" && (
          <>
            <p>
              New to Club Pass?{" "}
              <button type="button" onClick={() => switchMode("signup")} className={textLinkClass}>
                Create an account
              </button>
            </p>
            <p>
              <button type="button" onClick={() => switchMode("magic")} className="hover:text-zinc-900">
                Email me a sign-in link instead
              </button>
            </p>
          </>
        )}
        {mode === "signup" && (
          <p>
            Already have an account?{" "}
            <button type="button" onClick={() => switchMode("signin")} className={textLinkClass}>
              Sign in
            </button>
          </p>
        )}
        {(mode === "forgot" || mode === "magic") && (
          <p>
            <button type="button" onClick={() => switchMode("signin")} className={textLinkClass}>
              Back to sign in
            </button>
          </p>
        )}
      </div>
    </AuthShell>
  );
};

export default LoginPage;
